import { useState } from 'react';
import type { StockMove } from '@/types/odoo';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { PICKING_STATE_LABELS, getAvailabilityBadge, productName, unmatchedMoves, type PickingGroup } from '@/utils/materiaal';

interface MateriaalPickingTableProps {
  group: PickingGroup;
  defaultOpen: boolean;
  canDelete: boolean;
  onDelete: () => Promise<void>;
  moves: StockMove[];
}

/**
 * One picking's expandable lines table + delete control. Dumb component:
 * all data arrives via props, delete is delegated to the consumer.
 */
export function MateriaalPickingTable({
  group,
  defaultOpen,
  canDelete,
  onDelete,
  moves,
}: MateriaalPickingTableProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [deleting, setDeleting] = useState(false);
  const { picking, lines } = group;
  const stateLabel = PICKING_STATE_LABELS[picking.state] ?? picking.state;

  // IN pickings in projects are always retours (werf → magazijn). They carry no
  // studio materiaallijst lines — their products live only in the stock.moves —
  // so render those instead, with no availability column.
  const isRetour = picking.picking_type_code === 'incoming';
  // Items added straight to the picking in Odoo after the oplijst was uploaded —
  // absent from the authored lines. Appended below the lines for OUT pickings so
  // nothing on the actual delivery is invisible. Retours render every move already.
  const addedMoves = isRetour ? [] : unmatchedMoves(picking, lines, moves);
  const rowCount = isRetour ? moves.length : lines.length + addedMoves.length;

  const showDelete = canDelete && picking.id !== 0 && picking.state !== 'done';

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Materiaallijst ${picking.name} verwijderen? Dit kan niet ongedaan worden.`)) return;
    setDeleting(true);
    await onDelete();
    setDeleting(false);
  };

  return (
    <div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex-1 min-w-0 text-left rounded-lg border border-gray-100 bg-white hover:border-td-blue/30 hover:shadow-sm px-3 py-2 transition-all cursor-pointer"
        >
          <div className="flex items-center gap-2 min-w-0">
            <svg
              className={`w-3.5 h-3.5 text-td-muted shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-sm font-bold text-td-navy truncate">
              {picking.name}
            </span>
            {isRetour && (
              <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                Retour
              </span>
            )}
            {picking.create_date && (
              <span className="text-xs text-td-muted">
                {new Date(picking.create_date).toLocaleDateString('nl-BE', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            )}
            <StatusBadge stage={stateLabel} />
            {picking.carrier_tracking_ref && (
              <span className="text-xs text-td-muted truncate hidden sm:inline">
                {picking.carrier_tracking_ref}
              </span>
            )}
            <span className="shrink-0 text-[10px] font-bold text-td-muted bg-gray-100 px-1.5 py-0.5 rounded ml-auto">
              {rowCount}
            </span>
          </div>
        </button>
        {showDelete && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
            aria-label={`Verwijder materiaallijst ${picking.name}`}
          >
            {deleting ? (
              <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        )}
      </div>

      {open && rowCount > 0 && (
        <div className="mt-1.5 ml-6">
          <div className="rounded-xl overflow-hidden border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th scope="col" className="text-left px-4 py-2.5 text-[10px] font-bold text-td-muted uppercase tracking-wide">
                    Product
                  </th>
                  <th scope="col" className="text-right px-4 py-2.5 text-[10px] font-bold text-td-muted uppercase tracking-wide">
                    Aantal
                  </th>
                  <th scope="col" className="text-left px-4 py-2.5 text-[10px] font-bold text-td-muted uppercase tracking-wide">
                    Eenheid
                  </th>
                  {/* Retours have no reservation status — only leveringen show it. */}
                  {!isRetour && (
                    <th scope="col" className="text-left px-4 py-2.5 text-[10px] font-bold text-td-muted uppercase tracking-wide">
                      Status
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {isRetour
                  ? moves.map((move) => (
                      <tr key={move.id} className="border-t border-gray-100 hover:bg-gray-50/50">
                        <td className="px-4 py-2 text-td-navy">
                          {move.product_id ? move.product_id[1] : (move.description_picking || 'Onbekend product')}
                        </td>
                        <td className="px-4 py-2 text-td-navy text-right whitespace-nowrap">
                          {Number.isInteger(move.product_uom_qty) ? move.product_uom_qty : move.product_uom_qty.toFixed(2)}
                        </td>
                        <td className="px-4 py-2 text-td-navy whitespace-nowrap">
                          {move.product_uom ? move.product_uom[1] : '-'}
                        </td>
                      </tr>
                    ))
                  : [
                      ...lines.map((line) => {
                        const badge = getAvailabilityBadge(line, picking, moves);
                        return (
                          <tr key={`line-${line.id}`} className="border-t border-gray-100 hover:bg-gray-50/50">
                            <td className="px-4 py-2 text-td-navy">{productName(line)}</td>
                            <td className="px-4 py-2 text-td-navy text-right whitespace-nowrap">
                              {Number.isInteger(line.x_aantal) ? line.x_aantal : line.x_aantal.toFixed(2)}
                            </td>
                            <td className="px-4 py-2 text-td-navy whitespace-nowrap">
                              {line.x_eenheid ? line.x_eenheid[1] : '-'}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${badge.className}`}>
                                {badge.label}
                              </span>
                            </td>
                          </tr>
                        );
                      }),
                      // Moves added directly in Odoo — display the move's OWN unit
                      // (product_uom), not the lines' unit, and flag them so it's
                      // clear they weren't part of the uploaded oplijst.
                      ...addedMoves.map((move) => (
                        <tr key={`move-${move.id}`} className="border-t border-gray-100 hover:bg-gray-50/50">
                          <td className="px-4 py-2 text-td-navy">
                            {move.product_id ? move.product_id[1] : (move.description_picking || 'Onbekend product')}
                          </td>
                          <td className="px-4 py-2 text-td-navy text-right whitespace-nowrap">
                            {Number.isInteger(move.product_uom_qty) ? move.product_uom_qty : move.product_uom_qty.toFixed(2)}
                          </td>
                          <td className="px-4 py-2 text-td-navy whitespace-nowrap">
                            {move.product_uom ? move.product_uom[1] : '-'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                              Toegevoegd in Odoo
                            </span>
                          </td>
                        </tr>
                      )),
                    ]}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
